/**
 * View model for inline agent document draft preview (chat card + side panel).
 */

import localApiAdapter from '../../core/services/localApiAdapter';
import { formatBrandMoney, getBrandLocale } from '../../shared/utils/brandCurrency';
import i18n from '../../i18n';
import { getAgentDocumentTypeLabel } from '../services/agentDocumentHandoff';

const DEFAULT_TVA_RATE = 19;

const p = (key, opts) => i18n.t(`modules.shared.aiAgent.documentDraftView.${key}`, opts);

export function formatAgentDraftAmount(value) {
  return formatBrandMoney(value);
}

export function formatAgentDraftDate(isoDate) {
  if (!isoDate) return '—';
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return String(isoDate);
  return d.toLocaleDateString(getBrandLocale());
}

function computeDraftTotals(items, tvaRate = DEFAULT_TVA_RATE) {
  const subtotalHt = (items || []).reduce(
    (sum, item) => sum + (Number(item.total) || Number(item.quantity) * Number(item.price) || 0),
    0,
  );
  const taxAmount = Math.round(subtotalHt * (tvaRate / 100) * 100) / 100;
  const totalTtc = Math.round((subtotalHt + taxAmount) * 100) / 100;
  return { subtotalHt, taxAmount, totalTtc, tvaRate };
}

async function loadPartnerMeta(conversionData = {}) {
  const isSupplier = Boolean(conversionData.supplier || conversionData.supplierName);
  const partnerId = isSupplier ? conversionData.supplier : conversionData.client;
  const fallbackName =
    conversionData.clientName || conversionData.supplierName || '—';

  if (!partnerId) {
    return {
      name: fallbackName,
      ice: '',
      address: '',
      phone: '',
    };
  }

  try {
    const path = isSupplier ? '/facturation/fournisseurs' : '/facturation/clients';
    const rows = await localApiAdapter.get(path);
    const list = Array.isArray(rows) ? rows : [];
    const match = list.find((row) => String(row.id) === String(partnerId));

    if (!match) {
      return { name: fallbackName, ice: '', address: '', phone: '' };
    }

    return {
      name: match.company_name || match.name || match.companyName || fallbackName,
      ice: match.ice || match.nif || match.tax_id || '',
      address: match.address || match.adresse || '',
      phone: match.phone1 || match.phone || match.mobile || '',
    };
  } catch {
    return { name: fallbackName, ice: '', address: '', phone: '' };
  }
}

export async function buildAgentDocumentDraftView({ docType, conversionData, openPath }) {
  const items = Array.isArray(conversionData?.items) ? conversionData.items : [];
  const partner = await loadPartnerMeta(conversionData);
  const totals = computeDraftTotals(items);

  return {
    docType,
    docLabel: getAgentDocumentTypeLabel(docType),
    openPath,
    conversionData,
    partner,
    date: conversionData?.date || new Date().toISOString().split('T')[0],
    dueDate: conversionData?.dueDate || null,
    items: items.map((item, index) => {
      const quantity = Number(item.quantity) || 1;
      const priceHt = Number(item.price) || 0;
      const lineTotalHt = Number(item.total) || quantity * priceHt;
      const lineTax = Math.round(lineTotalHt * (totals.tvaRate / 100) * 100) / 100;
      return {
        id: item.id || index + 1,
        description: item.product || item.name || item.productName || p('line_fallback', { index: index + 1 }),
        quantity,
        priceHt,
        tvaRate: totals.tvaRate,
        totalTtc: Math.round((lineTotalHt + lineTax) * 100) / 100,
      };
    }),
    lineCount: items.length,
    totals,
  };
}

export function persistAgentDocumentDraft(conversionData) {
  if (!conversionData) return false;
  try {
    sessionStorage.setItem('documentConversionData', JSON.stringify(conversionData));
    return true;
  } catch (error) {
    console.error('Agent document draft persist failed', error);
    return false;
  }
}
